import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { storage, db } from '../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import './Kyc.css';
import './BoxKyc.css';
import { FaEye } from 'react-icons/fa'; // Import the FontAwesome icons


const UserKYC = () => {
    const { userId } = useParams(); // Get userId from the URL
    const [tierOne, setTierOne] = useState('');
    const [tierTwo, setTierTwo] = useState('');
    const [role, setRole] = useState('');
    const [group, setGroup] = useState('');
    const [plan, setPlan] = useState('');
    const [tierOneImageUrl, setTierOneImageUrl] = useState('');
    const [tierTwoImageUrl, setTierTwoImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchUserKYC = async () => {
            const userDocRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const data = userDoc.data();
                setTierOne(data.tierOne || '');
                setTierTwo(data.tierTwo || '');
                setRole(data.role || '');
                setPlan(data.plan || '');
                setGroup(data.group || '');
                setTierOneImageUrl(data.tierOneImageUrl || '');
                setTierTwoImageUrl(data.tierTwoImageUrl || '');
            }
        };

        fetchUserKYC();
    }, [userId]);

    const handleImageUpload = (e, tier) => {
        setLoading(true);
        const file = e.target.files[0];
        if (!file) return;

        const storageRef = ref(storage, `escrow_images/${tier}/${userId}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log(`Upload is ${progress}% done`);
            },
            (error) => {
                console.error('Error uploading image:', error);
                setLoading(false);
                setErrorMessage('Error uploading image.');
                setTimeout(() => setErrorMessage(''), 6000);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                if (tier === 'tier1') {
                    setTierOneImageUrl(downloadURL);
                } else if (tier === 'tier2') {
                    setTierTwoImageUrl(downloadURL);
                }
                setLoading(false);
            }
        );
    };

    const handleDeleteImage = async (tier) => {
        setLoading(true);
        try {
            const imageUrl = tier === 'tier1' ? tierOneImageUrl : tierTwoImageUrl;
            if (imageUrl) {
                const imageRef = ref(storage, imageUrl);
                await deleteObject(imageRef);

                if (tier === 'tier1') {
                    setTierOneImageUrl('');
                } else if (tier === 'tier2') {
                    setTierTwoImageUrl('');
                }

                const userDocRef = doc(db, 'users', userId);
                await setDoc(
                    userDocRef,
                    {
                        [`${tier}ImageUrl`]: '',
                    },
                    { merge: true }
                );

                setSuccessMessage(`Image deleted successfully for ${tier.toUpperCase()}.`);
                setTimeout(() => setSuccessMessage(''), 6000);
            }
        } catch (error) {
            console.error('Error deleting image:', error);
            setErrorMessage('Failed to delete image. Please try again.');
            setTimeout(() => setErrorMessage(''), 6000);
        }
        setLoading(false);
    };

    const handleTierChange = (e, tier) => {
        const value = e.target.value;
        if (tier === 'tier1') {
            setTierOne(value);
        } else if (tier === 'tier2') {
            setTierTwo(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const userDocRef = doc(db, 'users', userId);
            await setDoc(
                userDocRef,
                {
                    tierOne,
                    role,
                    plan,
                    group,
                    tierOneImageUrl,
                    tierTwo,
                    tierTwoImageUrl,
                },
                { merge: true }
            );
            setSuccessMessage('KYC data submitted successfully.');
            setTimeout(() => setSuccessMessage(''), 6000);
            setIsEditMode(false);
        } catch (error) {
            console.error('Error submitting KYC data:', error);
            setErrorMessage('Failed to submit KYC. Please try again.');
            setTimeout(() => setErrorMessage(''), 6000);
        }
        setLoading(false);
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
        setSuccessMessage('');
        setErrorMessage('');
    };

    const handleRoleChange = (e) => setRole(e.target.value);
    const handleGroupChange = (e) => setGroup(e.target.value);
    const handlePlanChange = (e) => setPlan(e.target.value);

    return (
        <div className='pageCover'>

            {successMessage && <div className="success-message">{successMessage}</div>}
            {errorMessage && <div className="error-message">{errorMessage}</div>}

            {isEditMode ? (
                <>
                    <form onSubmit={handleSubmit} className="kyc-form">
                        <div className="kyc-field">
                            <label>Role:</label>
                            <select
                                type="text"
                                value={role}
                                onChange={handleRoleChange}
                                className="kyc-text-input"
                            >
                                <option value="">- Select User Plan -</option>
                                <option value="vendor">Vendor</option>
                                <option value="customer">Customer</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>

                        {/* Plan */}
                        <div className="kyc-field">
                            <label>Account Plan:</label>
                            <select
                                type="text"
                                value={plan}
                                onChange={handlePlanChange}
                                className="kyc-text-input"
                            >
                                <option value="">- Select User Plan -</option>
                                <option value="basic">Basic</option>
                                <option value="premium">Tier 1</option>
                                <option value="professional">Tier 2</option>
                            </select>
                        </div>

                        {/* Group */}
                        <div className="kyc-field">
                            <label>Group:</label>
                            <select
                                type="text"
                                value={group}
                                onChange={handleGroupChange}
                                className="kyc-text-input"
                            >
                                <option value="">- Select User Role -</option>
                                <option value="activated">activated</option>
                                <option value="deactivated">deactivated</option>
                            </select>
                        </div>



                        <div className="kyc-fields">
                            <div className="kyc-field">
                                <label>Tier 1:</label>
                                <select value={tierOne} onChange={(e) => handleTierChange(e, 'tier1')} className="kyc-select">
                                    <option value="">Select an option</option>
                                    <option value="NIN">National Identification Number</option>
                                    <option value="driversLicense">Driver's License</option>
                                    <option value="passport">Passport</option>
                                </select>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, 'tier1')}
                                    className="kyc-file-input"
                                />
                                {tierOneImageUrl && (
                                    <>
                                        <a className='success-message' href={tierOneImageUrl} target="_blank" rel="noopener noreferrer">View</a>
                                        <button
                                            type="button"
                                            className="error-message"
                                            onClick={() => handleDeleteImage('tier1')}
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>

                            <div className="kyc-field">
                                <label>Tier 2:</label>
                                <select value={tierTwo} onChange={(e) => handleTierChange(e, 'tier2')} className="kyc-select">
                                    <option value="">Select an option</option>
                                    <option value="bankStatement">Upload Bank Statement</option>
                                    <option value="utilityBill">Utility Bill</option>
                                    <option value="residencyCertificate">Certificate of Residency</option>
                                </select>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageUpload(e, 'tier2')}
                                    className="kyc-file-input"
                                />
                                {tierTwoImageUrl && (
                                    <>
                                        <a className='success-message' href={tierTwoImageUrl} target="_blank" rel="noopener noreferrer">View</a>
                                        <button
                                            type="button"
                                            className="error-message"
                                            onClick={() => handleDeleteImage('tier2')}
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Loading ...' : 'Submit'}
                        </button>
                    </form>
                </>
            ) : (
                <div>
                    <div className='kyc-box'>
                        <h4>Role: {role || 'No Roles!'}</h4>
                    </div>

                    <div className='kyc-box'>
                        <h4>Account Plan: {plan || 'Basic!'}</h4>
                    </div>

                    <div className='kyc-box'>
                        <h4>Status Group: {group || 'No Groups!'}</h4>
                    </div>

                    <div className='kyc-box'>
                        <h4>Tier 1: {tierOne || 'Not Verified!'}
                            {tierOneImageUrl && (
                                <a className='view-doc' href={tierOneImageUrl} target="_blank" rel="noopener noreferrer">
                                    <FaEye className="" /> View
                                </a>
                            )}
                        </h4>
                    </div>

                    <div className='kyc-box'>
                        <h4>Tier 2: {tierTwo || 'Not Verified!'}
                            {tierTwoImageUrl && (
                                <a className='view-doc' href={tierTwoImageUrl} target="_blank" rel="noopener noreferrer">
                                    <FaEye className="" />  View
                                </a>
                            )}</h4>
                    </div>
                </div>
            )}


            {!isEditMode && (
                <p className="center kycButton" onClick={toggleEditMode}>
                    Update Account
                </p>
            )}




        </div>
    );
};

export default UserKYC;
